/**
 * orchestrateKevia.js
 *
 * Central orchestrator — every LLM request from Kevia passes through here.
 *
 * Responsibilities:
 *  1. Resolve active tier + capabilities
 *  2. Trim and assemble context block (priority-preserving)
 *  3. Build conversation history prefix
 *  4. Detect intent from user message
 *  5. Assemble final LLM payload
 *  6. Return { payload, userPrompt, intent, mood, canLlm }
 *
 * Does NOT perform any fetch — callers do that.
 */

import { KEVIA_ACTIVE_TIER, KEVIA_SYSTEM_PROMPT } from "../kevia-identity";
import { resolveKeviaMood                        } from "./resolveKeviaMood";
import { buildRuntimeContext }                     from "../kevia-runtime/runtimeSelectors";
import { buildContextBudget }                      from "./contextBudgetManager";
import { buildTrustPromptAnnotation }              from "./trustSignals";
import { buildDigestPromptFragment }               from "../kevia-workflows/operationalDigest";
import { selectConversationalMode, getModeInstructions } from "./responseStyleEngine";
import { buildVerbosityBlock }                     from "./responseCompression";
import { buildMicroInsightHint }                   from "./contextualMicroInsights";
import { getMinistryPressure }                     from "../kevia-data/districtStateEngine";
import { buildDenominationalAddendum }             from "../kevia-egw/denominationalReasoningEngine";
import { buildPastoralAssessment }                 from "../kevia-ethics";
import { buildDiscernmentContext }                 from "../kevia-discernment/conversationalDiscernmentEngine";
import { buildFormationContext }                   from "../kevia-formation/formationConsistencyEngine";
import { buildJudgmentContext }                    from "../kevia-judgment/pastoralJudgmentCoordinator";
import { buildContinuityContext }                  from "../kevia-continuity/continuityCoordinator";
import { buildEcclesialContext }                   from "../kevia-ecclesia/ecclesialDiscernmentCoordinator";
import { buildPastoralStateContext }               from "../kevia-pastoral-state/pastoralStateCoordinator";
import { buildRefinementContext }                  from "../kevia-refinement/refinementCoordinator";
import { buildHumanRhythmContext }                 from "../kevia-human/humanRhythmCoordinator";
import { buildIntuitionContext }                   from "../kevia-intuition/intuitionCoordinator";

// Budget tokens per tier: basic keeps prompts shorter and cheaper
const TIER_BUDGET = { plus: 800, basic: 400, local: 0 };

/* ─────────────────────────────────────────────────────────────
   CONSTANTS
───────────────────────────────────────────────────────────── */

const CONTEXT_BLOCK_MAX = 600;  // chars
const HISTORY_MSG_MAX   = 200;  // chars per message in history prefix
const HISTORY_WINDOW    = 6;    // last N messages to include

/* ─────────────────────────────────────────────────────────────
   INTENT DETECTION
   Lightweight signal — not ML.
───────────────────────────────────────────────────────────── */

const INTENT_PATTERNS = [
  { intent: "sermon",          patterns: [/serm[oó]n/i, /pred[ií]ca/i, /texto b[ií]blico/i, /b[ií]blica/i] },
  { intent: "visit_plan",      patterns: [/visita/i, /visitar/i, /planear visita/i, /programa.*visita/i] },
  { intent: "prayer",          patterns: [/oraci[oó]n/i, /orar/i, /intercesi[oó]n/i] },
  { intent: "family_followup", patterns: [/familia/i, /seguimiento/i, /atenci[oó]n pastoral/i, /persona/i, /miembro/i, /contacto/i] },
  { intent: "reflection",      patterns: [/reflexi[oó]n/i, /meditar/i, /devocional/i, /espiritual/i] },
  { intent: "events",          patterns: [/evento/i, /actividad/i] },
  { intent: "planning",        patterns: [/planif/i, /agenda/i, /programar/i, /pr[oó]ximo/i, /siguiente/i, /hoy/i, /pendiente/i, /qu[eé]\s+tengo/i, /qu[eé]\s+sigue/i] },
  { intent: "analysis",        patterns: [/an[aá]lisis/i, /estad[ií]st/i, /resumen/i, /cu[aá]ntos/i, /tendencia/i, /catec[uú]meno/i, /membrec[ií]a/i, /membres[ií]a/i] },
  { intent: "theology",          patterns: [/doctrina/i, /creencia/i, /teolog[ií]a/i, /adventist/i, /s[aá]bado/i, /estado.*muertos/i, /santuario/i, /segunda.*venida/i, /gran conflicto/i, /bautismo/i] },
  { intent: "bible",             patterns: [/b[ií]blia/i, /vers[ií]culo/i, /escritura/i, /pasaje/i, /texto.*sagrado/i] },
  { intent: "counseling",        patterns: [/consejer[ií]a/i, /aconsejar/i, /orientar/i, /ayudar.*problema/i, /conflicto/i, /crisis/i, /matrimon/i, /divorcio/i] },
  { intent: "discipline",        patterns: [/disciplina/i, /disciplinar/i, /restaurar/i, /restauraci[oó]n/i, /falta/i, /pecado p[uú]blico/i, /excluir/i, /exclusi[oó]n/i] },
  { intent: "membership",        patterns: [/membri[eé]s[ií]a/i, /trasladar/i, /carta de traslado/i, /recibir.*miembro/i, /nuevo.*miembro/i, /ingreso.*iglesia/i] },
  { intent: "church_organization", patterns: [/junta.*iglesia/i, /junta directiva/i, /anciano/i, /di[aá]cono/i, /diaconisa/i, /tesorero/i, /secretario.*iglesia/i, /comisi[oó]n/i, /elecci[oó]n/i, /nombramiento/i] },
  { intent: "leadership",        patterns: [/liderazgo/i, /l[ií]der/i, /coordinador.*iglesia/i, /director.*departamento/i, /pastor.*distrito/i] },
];

export function detectIntent(text) {
  for (const { intent, patterns } of INTENT_PATTERNS) {
    if (patterns.some((p) => p.test(text))) return intent;
  }
  return "general";
}

/* ─────────────────────────────────────────────────────────────
   CONTEXT TRIMMER
   Priority-preserving trim to CONTEXT_BLOCK_MAX chars.
───────────────────────────────────────────────────────────── */

function trimContextBlock(raw) {
  if (!raw || raw.length <= CONTEXT_BLOCK_MAX) return raw ?? "";

  const lines = raw.split("\n");
  const kept  = [];
  let   used  = 0;

  for (const line of lines) {
    const isFirstLine = kept.length === 0;
    const isSignal    = /\?|urgente|alta|activa|sin visita|sin bautismo/i.test(line);
    const wouldFit    = used + line.length + 1 <= CONTEXT_BLOCK_MAX;

    if (isFirstLine || isSignal || wouldFit) {
      kept.push(line);
      used += line.length + 1;
    }
    if (used >= CONTEXT_BLOCK_MAX) break;
  }

  return kept.join("\n");
}

/* ─────────────────────────────────────────────────────────────
   HISTORY PREFIX
───────────────────────────────────────────────────────────── */

function buildHistoryPrefix(msgs) {
  if (!msgs || msgs.length === 0) return "";
  // Filter out action_proposal and any other structural messages that lack content
  const recent = msgs.filter((m) => m.content != null).slice(-HISTORY_WINDOW);
  if (recent.length === 0) return "";
  const lines  = recent.map((m) => {
    const role    = m.role === "user" ? "Usuario" : "Kevia";
    const content = m.content.length > HISTORY_MSG_MAX
      ? m.content.slice(0, HISTORY_MSG_MAX) + "…"
      : m.content;
    return `${role}: ${content}`;
  });
  return `[Conversación reciente]\n${lines.join("\n")}\n\n[Mensaje actual]\n`;
}

/* ─────────────────────────────────────────────────────────────
   PUBLIC API
───────────────────────────────────────────────────────────── */

export function orchestrateKevia({ text, entityContext = null, historyMsgs = [], runtimeState = {}, tier = null, appScope = null, dataContext = null, workflowContext = null, digestContext = null, operationalContext = null, synthesisContext = null, proactiveContext = null, relationshipContext = null, trajectoryContext = null, autonomyContext = null, temporalIntent = null, workflowActive = false, bibleQuery = null, denominationalPlan = null }) {
  const activeTier  = tier ?? KEVIA_ACTIVE_TIER;
  const canLlm      = activeTier.can?.llm      ?? false;
  const canStream   = activeTier.can?.streaming ?? false;
  const intent      = detectIntent(text);

  const mood = resolveKeviaMood(entityContext, runtimeState);

  // Compact app context prefix — tells Kevia what the user is currently doing
  let appPrefix = "";
  if (appScope) {
    const parts = [];
    if (appScope.module && appScope.module !== "inicio") parts.push(`módulo: ${appScope.module}`);
    if (appScope.role)                                   parts.push(`rol: ${appScope.role}`);
    if (parts.length > 0) appPrefix = `[${parts.join(" | ")}]\n`;
  }

  // Runtime cognition prefix — compact situational awareness from the session timeline
  const runtimeCtx = buildRuntimeContext();

  // FASE 23.3 — Budget-managed context prefixes (prevents context collapse)
  const budgeted = buildContextBudget({
    runtimeContext:  runtimeCtx  ?? null,
    dataContext:     dataContext  ?? null,
    workflowContext: workflowContext ?? null,
  });

  const dataPrefix     = budgeted.dataContext     ? budgeted.dataContext     + "\n" : "";
  const workflowPrefix = budgeted.workflowContext ? budgeted.workflowContext + "\n" : "";
  // runtimeCtx already built above — use budgeted version
  const runtimePrefixFinal = budgeted.runtimeContext ? budgeted.runtimeContext + "\n" : "";

  // Operational context — the ambient pastoral snapshot loaded on Kevia open.
  // Always present (when available) regardless of per-message intent.
  // Injected BEFORE per-message data so the model has the full picture first.
  const operationalPrefix = operationalContext ? operationalContext + "\n" : "";

  // Proactive context — ambient pastoral insights from district state engine.
  // Injected after the operational panorama; always present when there are
  // high-priority district signals regardless of the current question.
  const proactivePrefix = proactiveContext
    ? proactiveContext.slice(0, 350) + "\n"
    : "";

  // Relationship context — per-entity longitudinal pastoral memory.
  // [relationship: ...] and [trajectory: ...] when an entity is active in conversation.
  // Injected AFTER [proactive] so entity-specific state takes contextual precedence.
  const relPrefix      = relationshipContext ? relationshipContext.slice(0, 240) + "\n" : "";
  const trajPrefix     = trajectoryContext   ? trajectoryContext.slice(0, 200)   + "\n" : "";

  // Autonomy context — top pastoral priority signal from the autonomy layer.
  // Only injected when a meaningful signal exists (signal-gated, cooldown-protected).
  const autonomyPrefix = autonomyContext     ? autonomyContext.slice(0, 240)     + "\n" : "";

  // Synthesis context — multi-tool fusion for broad or complex queries.
  // Injected AFTER proactive context, BEFORE per-message data.
  // Capped at 450 chars to stay within budget even without budget manager.
  const synthesisPrefix = synthesisContext
    ? synthesisContext.slice(0, 450) + "\n"
    : "";

  // FASE 24 — Trust annotation + operational digest
  const trustAnnotation = buildTrustPromptAnnotation({
    hasLiveData:       !!dataContext,
    hasWorkflows:      !!workflowContext,
    hasRuntimeContext: !!runtimeCtx,
  });
  const trustPrefix  = trustAnnotation ? trustAnnotation + "\n" : "";

  const digestFrag   = digestContext ?? buildDigestPromptFragment();
  const digestPrefix = digestFrag ? digestFrag + "\n" : "";

  const historyPrefix = buildHistoryPrefix(historyMsgs);
  const rawCtxBlock   = entityContext?.contextBlock ?? "";
  const trimmedCtx    = trimContextBlock(rawCtxBlock);
  const userPrompt    = appPrefix + runtimePrefixFinal + operationalPrefix + proactivePrefix + relPrefix + trajPrefix + autonomyPrefix + synthesisPrefix + dataPrefix + workflowPrefix + trustPrefix + digestPrefix + historyPrefix + trimmedCtx + text;

  // ── Conversational personality layer ──────────────────────────────
  // Selects mode and injects verbosity limits + micro-context hint into the system prompt.
  const pressure   = getMinistryPressure();
  const convMode   = selectConversationalMode({ intent, mood: mood.mood, pressure, temporalIntent, workflowActive });
  const styleBlock = getModeInstructions(convMode) + "\n" + buildVerbosityBlock(convMode);
  const microHint  = buildMicroInsightHint(intent, convMode);

  if (!canLlm) {
    return { payload: null, userPrompt, intent, mood, canLlm: false, canStream: false, convMode };
  }

  // Compose system prompt: base + conversational personality layer + denominational context
  const styleSection     = "\n\n" + styleBlock + (microHint ? "\n" + microHint : "");
  const denomAddendum    = buildDenominationalAddendum(denominationalPlan);

  const assessment       = buildPastoralAssessment({
    text,
    intent,
    mood:               mood.mood,
    entityContext,
    bibleQuery,
    denominationalPlan,
  });

  // FASE 6 — Conversational discernment: depth, timing, silence, question, empathy, wisdom
  // Reuses assessment.emotion to avoid re-running emotion detection.
  const discernment      = buildDiscernmentContext({
    text,
    intent,
    emotion:            assessment.emotion,
    mood:               mood.mood,
    riskLevel:          assessment.riskLevel,
    ministryLoad:       null,
    conversationLength: historyMsgs?.length ?? 0,
  });

  const discernmentSection = discernment.discernmentBlock
    ? "\n\n" + discernment.discernmentBlock
    : "";

  // FASE 7 — Spiritual formation: presence, posture, theological centering, hope stability
  // Consumes assessment.emotion and discernment outputs — no duplicate detection.
  const formation        = buildFormationContext({
    text,
    intent,
    emotion:            assessment.emotion,
    mood:               mood.mood,
    riskLevel:          assessment.riskLevel,
    wisdomTone:         discernment.wisdomTone,
    timing:             discernment.timing,
    conversationLength: historyMsgs?.length ?? 0,
  });

  const formationSection = formation.formationBlock
    ? "\n\n" + formation.formationBlock
    : "";

  // FASE 8 — Pastoral judgment: reasoning, tension detection, principle prioritization
  // Consumes assessment.emotion + assessment.ethics + discernment + formation outputs.
  const judgment         = buildJudgmentContext({
    text,
    intent,
    emotion:             assessment.emotion,
    mood:                mood.mood,
    riskLevel:           assessment.riskLevel,
    ethics:              assessment.ethics,
    discernmentTiming:   discernment.timing,
    discernmentDepth:    discernment.depth,
    formationPresence:   formation.presence,
    conversationLength:  historyMsgs?.length ?? 0,
  });

  const judgmentSection  = judgment.judgmentBlock
    ? "\n\n" + judgment.judgmentBlock
    : "";

  // FASE 9 — Longitudinal continuity: spiritual journey memory, patterns, temporal arc.
  // Runs AFTER judgment — has access to all prior layer outputs.
  // Reads entity history from localStorage (synchronous, <5ms).
  const continuity       = buildContinuityContext({
    entityId:           entityContext?.id    ?? null,
    entityType:         entityContext?.type  ?? null,
    text,
    intent,
    emotion:            assessment.emotion,
    riskLevel:          assessment.riskLevel,
    processState:       judgment.processState,
    complexity:         judgment.complexity,
    trajectoryStage:    "desconocido",     // trajectory resolved earlier in runtimeCognitiveLayer
    discernmentTiming:  discernment.timing,
    formationPresence:  formation.presence,
    conversationLength: historyMsgs?.length ?? 0,
  });

  const continuitySection = continuity.continuityContext
    ? "\n\n" + continuity.continuityContext
    : "";

  // FASE 10 — Ecclesial relational intelligence: family dynamics, congregational health,
  // pastoral load distribution, conflict signals, care network, silent tensions.
  // Runs AFTER continuity — most context-rich position; reads districtState synchronously.
  const ecclesia         = buildEcclesialContext({
    text,
    intent,
    emotion:             assessment.emotion,
    riskLevel:           assessment.riskLevel,
    ethics:              assessment.ethics,
    entityContext,
    conversationLength:  historyMsgs?.length ?? 0,
    continuityPattern:   continuity.pattern,
    judgmentComplexity:  judgment.complexity,
  });

  const ecclesiaSection  = ecclesia.ecclesialContext
    ? "\n\n" + ecclesia.ecclesialContext
    : "";

  // FASE R3 — Unified pastoral state: exhaustion, compassionate fatigue, isolation,
  // over-responsibility, emotional capacity, escalation/referral.
  // Merges FASE 11 (adaptive guidance) + FASE 12 (sustainability) into one compact coordinator.
  const pastoralState    = buildPastoralStateContext({
    text,
    intent,
    emotion:             assessment.emotion,
    riskLevel:           assessment.riskLevel,
    processState:        judgment.processState,
    complexity:          judgment.complexity,
    regressionState:     continuity.regressionState,
    conflictType:        ecclesia.conflictType,
    congregationSignal:  ecclesia.congregationSignal,
    loadState:           ecclesia.loadState,
    silentTension:       ecclesia.silentTension,
    familyDynamic:       ecclesia.familyDynamic,
    entityContext,
    conversationLength:  historyMsgs?.length ?? 0,
  });

  const pastoralStateSection = pastoralState.pastoralStateContext
    ? "\n\n" + pastoralState.pastoralStateContext
    : "";

  // FASE R1 — Architectural coherence + signal compression.
  // Receives ALL prior block texts; produces ≤350-char consolidation instruction.
  const activeBlocks = [
    assessment.systemBlock,
    discernment.discernmentBlock,
    formation.formationBlock,
    judgment.judgmentBlock,
    continuity.continuityContext,
    ecclesia.ecclesialContext,
    pastoralState.pastoralStateContext,
  ].filter(Boolean);

  const refinement = buildRefinementContext({
    riskLevel:        assessment.riskLevel,
    emotion:          assessment.emotion,
    complexity:       judgment.complexity,
    silentTension:    ecclesia.silentTension,
    exhaustionState:  pastoralState.exhaustionState,
    isolationState:   pastoralState.isolationState,
    rhythmState:      null,
    regressionState:  continuity.regressionState,
    conflictType:     ecclesia.conflictType,
    loadState:        ecclesia.loadState,
    boundaryState:    null,
    escalation:       pastoralState.escalation,
    pace:             null,
    formationPresence: formation.presence,
    guidanceType:     null,
    timing:           discernment.timing,
    depth:            discernment.depth,
    capacity:         pastoralState.capacity,
    readiness:        null,
    convMode,
    familyDynamic:    ecclesia.familyDynamic,
    activeBlockCount: activeBlocks.length,
    blockTexts:       activeBlocks,
  });

  const refinementSection = refinement.refinementContext
    ? "\n\n" + refinement.refinementContext
    : "";

  // FASE R7 — Intuitive signal prioritization + cognitive quieting.
  // Collapses all active signals to 1 dominant impulse, suppresses over-analysis.
  // Runs AFTER refinement, BEFORE humanRhythm.
  const intuition = buildIntuitionContext({
    emotion:            assessment.emotion,
    riskLevel:          assessment.riskLevel,
    processState:       judgment.processState,
    exhaustionState:    pastoralState.exhaustionState,
    conflictType:       ecclesia.conflictType,
    continuityPattern:  continuity.pattern,
    silentTension:      ecclesia.silentTension,
    intent,
    capacity:           pastoralState.capacity,
    activeBlockCount:   activeBlocks.length,
    signalCount:        refinement.signalCount  ?? 0,
    loadLevel:          refinement.loadLevel    ?? "low",
    refinementActive:   refinement.isActive     ?? false,
    userLength:         text?.length            ?? 0,
    conversationLength: historyMsgs?.length     ?? 0,
  });

  const intuitionSection = intuition.intuitionContext
    ? "\n\n" + intuition.intuitionContext
    : "";

  // FASE R6 — Human rhythm: understatement, cadence variation, emotional breathing.
  // Receives minimalismLevel from R7 so it can suppress cadence variation when
  // the minimalism gate is already constraining the response.
  const humanRhythm = buildHumanRhythmContext({
    emotion:            assessment.emotion,
    exhaustionState:    pastoralState.exhaustionState,
    capacity:           pastoralState.capacity,
    riskLevel:          assessment.riskLevel,
    processState:       judgment.processState,
    intent,
    sessionLoad:        refinement.loadLevel ?? "low",
    conversationLength: historyMsgs?.length ?? 0,
    historyMsgs:        historyMsgs ?? [],
    minimalismLevel:    intuition.minimalismLevel,
  });

  const rhythmSection = humanRhythm.rhythmContext
    ? "\n\n" + humanRhythm.rhythmContext
    : "";

  const systemPrompt     = KEVIA_SYSTEM_PROMPT + styleSection + denomAddendum + assessment.systemBlock + discernmentSection + formationSection + judgmentSection + continuitySection + ecclesiaSection + pastoralStateSection + refinementSection + intuitionSection + rhythmSection;

  const budget = TIER_BUDGET[activeTier.id] ?? 400;

  const payload = {
    provider:     "openai",
    scope:        "chat",
    intent,
    kevia_tier:   activeTier.id,
    promptPacket: {
      system: systemPrompt,
      user:   userPrompt,
    },
    budget:             { user: budget },
    bibleQuery:         bibleQuery ?? undefined,
    denominationalPlan: denominationalPlan ?? undefined,
  };

  return { payload, userPrompt, intent, mood, canLlm: true, canStream, convMode, riskLevel: assessment.riskLevel, discernment, formation, judgment, continuity, ecclesia, pastoralState, refinement, intuition, humanRhythm };
}
